import { Badge, Group, Stack, Text } from '@mantine/core'
import type { ProjectTagRow } from '@/types/api'

function tagNames(ids: string[] | null | undefined, tags: ProjectTagRow[]): string[] {
  if (!Array.isArray(ids) || ids.length === 0) return []
  const byId = new Map(tags.map((t) => [t.id, t.name || t.id]))
  return ids.map((id) => byId.get(id) || id)
}

type EmergencyCurrentSettingsProps = {
  ticketScope?: string | null
  excludedTagIds?: string[] | null
  includedTagIds?: string[] | null
  tags: ProjectTagRow[]
}

export function EmergencyCurrentSettings({
  ticketScope,
  excludedTagIds,
  includedTagIds,
  tags,
}: EmergencyCurrentSettingsProps) {
  const scope = String(ticketScope || 'all').trim().toLowerCase()
  const excluded = tagNames(excludedTagIds, tags)
  const included = tagNames(includedTagIds, tags)

  return (
    <Stack gap={6}>
      <Text size="sm" fw={600}>
        Сейчас в аварии
      </Text>
      <Stack gap={4}>
        <Group gap={6} wrap="wrap">
          <Text size="xs" c="dimmed" w={140}>
            Не назначенные
          </Text>
          <Badge size="sm" variant="light" color={scope === 'unassigned' ? 'orange' : 'gray'}>
            {scope === 'unassigned' ? 'Да — new/open без исполнителя' : 'Нет'}
          </Badge>
        </Group>
        <Group gap={6} align="flex-start" wrap="wrap">
          <Text size="xs" c="dimmed" w={140}>
            Фильтр меток
          </Text>
          {included.length > 0 ? (
            <Stack gap={4}>
              <Text size="xs">Только с метками:</Text>
              <Group gap={4}>
                {included.map((name) => (
                  <Badge key={`inc-${name}`} size="xs" variant="light" color="blue">
                    {name}
                  </Badge>
                ))}
              </Group>
            </Stack>
          ) : excluded.length > 0 ? (
            <Stack gap={4}>
              <Text size="xs">Исключены:</Text>
              <Group gap={4}>
                {excluded.map((name) => (
                  <Badge key={`exc-${name}`} size="xs" variant="light" color="red">
                    {name}
                  </Badge>
                ))}
              </Group>
            </Stack>
          ) : (
            <Text size="xs">
              {scope === 'unassigned'
                ? 'Все неназначенные new/open (без фильтра по меткам)'
                : 'Все обращения (без фильтра по меткам)'}
            </Text>
          )}
        </Group>
      </Stack>
    </Stack>
  )
}

export type EmergencyTagFilterMode = 'all' | 'exclude' | 'include'

export function tagFilterModeFromEmergency(
  excluded?: string[] | null,
  included?: string[] | null,
): EmergencyTagFilterMode {
  if (Array.isArray(included) && included.length > 0) return 'include'
  if (Array.isArray(excluded) && excluded.length > 0) return 'exclude'
  return 'all'
}

export function tagFilterPayload(mode: EmergencyTagFilterMode, selectedTagIds: string[]) {
  const ids = selectedTagIds.length ? selectedTagIds : []
  if (mode === 'exclude') {
    return { excluded_tag_ids: ids, included_tag_ids: [] as string[] }
  }
  if (mode === 'include') {
    return { excluded_tag_ids: [] as string[], included_tag_ids: ids }
  }
  return { excluded_tag_ids: [] as string[], included_tag_ids: [] as string[] }
}
